import { getSortedUniqueIds } from '@/helpers/getIds'
import { client } from '@/lib/sanity'
import { isEqual } from 'lodash'
import { useCallback, useEffect, useState } from 'react'
import { useThrottledCallback } from 'sanity'

// prepare a preview client
const previewClient = client.withConfig({
  withCredentials: true,
  perspective: 'previewDrafts',
  useCdn: false,
})

export default function useLiveQuery<T>(
  query: string,
  params: Record<string, unknown> | undefined,
  initialData: T,
) {
  const [data, setData] = useState(initialData)
  const [loading, setLoading] = useState(false)
  const [keys, setKeys] = useState<string[]>(getSortedUniqueIds(initialData))
  const [queryParams, setQueryParams] = useState<typeof params>(params)

  // update query params if they change (using isEqual to avoid unnecessary updates)
  useEffect(() => {
    if (isEqual(queryParams, params)) {
      return
    }
    setQueryParams(params)
  }, [params, queryParams])

  // function that refreshes the data
  const refresh = useCallback(() => {
    setLoading(true)
    previewClient
      .fetch(query, queryParams)
      .then((v: T) => {
        setData(v)

        const sortedIds = getSortedUniqueIds(v)
        if (!isEqual(keys, sortedIds)) {
          setKeys(sortedIds)
        }
      })
      .finally(() => {
        setLoading(true)
      })
  }, [keys, query, queryParams])

  const throttledRefresh = useThrottledCallback(refresh, 1500, { leading: true, trailing: true })

  // handle sanity listener mutation. Checks if the documentId is in the keys array
  const mutationHandler = useCallback(
    (event) => {
      if (
        !keys.includes(event.documentId) &&
        !keys.includes(event.documentId.replace(/^drafts\./, ''))
      ) {
        return
      }

      setLoading(true)
      throttledRefresh()
    },
    [keys, throttledRefresh],
  )

  // subscribe to sanity mutations
  useEffect(() => {
    const subscription = previewClient
      .listen('*', {}, { visibility: 'query', includeResult: false })
      .subscribe((e) => setTimeout(() => mutationHandler(e), 500))
    return () => subscription.unsubscribe()
  }, [mutationHandler])

  return {
    data,
    loading,
    refresh: () => {
      setLoading(true)
      throttledRefresh()
    },
  }
}
